// Urbanisme d'une collectivité sur le portail, sans compte (lot L5-C2, M18, D-259) : démarches d'urbanisme
// ouvertes (lien vers la fiche de la démarche M05, où se fait le dépôt), compétence décrite par la
// collectivité, et zones des plans en vigueur sur la carte. Aucune demande ni aucun demandeur n'y figure.
import { CarteTerritoire, EtatVide, Squelette } from '@cc/ui';
import { useEffect, useMemo, useState } from 'react';
import { Navigate, useParams } from 'react-router';
import { EtatRequete } from '../../../commun/EtatRequete';
import { construireEchelles, fichierPourEchelle, fichiersInitiaux, useLimites, type NomFichier } from '../../territoire/donnees';
import { useUrbanismePublic } from '../../urbanisme/api';
import { surfacesPubliques } from '../../urbanisme/logique';
import { useTerritoirePublic } from '../signalements/api';
import { codeCollectiviteValide } from '../collectivite';
import { Bloc, EnTeteCitoyen, Ligne, Liste } from '../commun';
import styles from '../citoyen.module.css';

function CarteZones({ code, surfaces }: { code: string; surfaces: ReturnType<typeof surfacesPubliques> }) {
  const territoire = useTerritoirePublic(code);
  const [supplementaires, setSupplementaires] = useState<NomFichier[]>([]);
  const fichiers = useMemo(() => [...new Set([...fichiersInitiaux(territoire.data), ...supplementaires])], [territoire.data, supplementaires]);
  const { limites, presentation, chargement } = useLimites(fichiers, fichiersInitiaux(territoire.data));
  const echelles = useMemo(() => (territoire.data && !chargement ? construireEchelles(territoire.data, limites, presentation) : null), [territoire.data, chargement, limites, presentation]);
  const [echelle, setEchelle] = useState<string | null>(null);
  useEffect(() => {
    if (echelles && !echelle) setEchelle(echelles.defaut);
  }, [echelles, echelle]);
  if (territoire.isError) return <p className={styles.aide}>Carte indisponible : la liste des zones ci-dessous reste consultable.</p>;
  if (!echelles || !echelle) return <Squelette hauteurBloc={260} libelle="Chargement de la carte" />;
  return (
    <CarteTerritoire
      titre="Zones des plans en vigueur"
      echelles={echelles.echelles}
      echelle={echelle}
      onEchelle={(id) => {
        const f = fichierPourEchelle(id);
        if (f) setSupplementaires((s) => (s.includes(f) ? s : [...s, f]));
        setEchelle(id);
      }}
      surfaces={surfaces}
      compact
      hauteur={280}
      libelleObjets={{ singulier: 'zone', pluriel: 'zones' }}
    />
  );
}

export function PageUrbanisme() {
  const { code: brut } = useParams();
  const code = codeCollectiviteValide(brut);
  const u = useUrbanismePublic(code);
  const surfaces = useMemo(() => surfacesPubliques(u.data), [u.data]);
  if (!code) return <Navigate to="/citoyen" replace />;
  const types = u.data?.types ?? [];
  const zones = u.data?.zones ?? [];
  return (
    <>
      <EnTeteCitoyen titre="Urbanisme" surTitre={u.data?.collectivite.nom} sousTitre="Démarches d'urbanisme de la collectivité et zones des plans en vigueur. Aucun titre foncier n'est délivré par ce service." retour={`/citoyen/${code}`} libelleRetour="Accueil" />
      <EtatRequete chargement={u.isPending} erreur={u.error} onReessayer={() => void u.refetch()} fonction="L'urbanisme de la collectivité" lignes={4}>
        <Bloc titre="Démarches d'urbanisme">
          {types.length === 0 ? (
            <EtatVide titre="Aucune démarche d'urbanisme ouverte"><p>Cette collectivité ne propose pas de démarche d&apos;urbanisme en ligne pour le moment.</p></EtatVide>
          ) : (
            <Liste libelle="Démarches d'urbanisme">
              {types.map((t) => <Ligne key={t.code} href={`/citoyen/${code}/demarches/${t.demarche_code}`} titre={`${t.libelle}${t.fictif ? ' (fictif)' : ''}`} sousTitre={t.competence} />)}
            </Liste>
          )}
        </Bloc>
        <Bloc titre="Zones des plans en vigueur">
          {zones.length === 0 ? <p className={styles.aide}>Aucun plan en vigueur n&apos;est publié.</p> : (
            <>
              <CarteZones code={code} surfaces={surfaces} />
              <ul className={styles.aide}>{zones.map((z) => <li key={z.code}>{`${z.code} · ${z.libelle}${z.fictif ? ' (fictif)' : ''} : ${z.vocation}`}</li>)}</ul>
            </>
          )}
        </Bloc>
      </EtatRequete>
    </>
  );
}
